import {
  FilterableSurveyCategory,
  SurveyCategory,
} from '@/client/security-culture/types';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { AppButton } from '@/ui/buttons';
import { BarChart } from '@/ui/charts/bar-chart';
import { FlexContainer } from '@/ui/styled-ui';
import {
  getSecurityCultureChartWidth,
  parseFloatingNumber,
} from '@/utils/helpers/ui.helper';
import { Skeleton } from 'primereact/skeleton';
import { Tooltip } from 'primereact/tooltip';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledContainer = styled(FlexContainer)`
  height: 100%;
  padding-bottom: calc(2 * var(--default-padding));
  position: relative;
  min-width: max-content;

  > div {
    position: absolute;
    bottom: calc(-1 * var(--xxsmall-padding));
  }
`;

const StyledLabelContainer = styled(FlexContainer)<{
  isMainDashboard?: boolean;
}>`
  white-space: nowrap;
  align-self: center;

  ${({ isMainDashboard }) =>
    isMainDashboard &&
    `
    font-size: 12px;
    font-weight: 500;
  `}

  @media screen and (max-width: 1620px) {
    white-space: pre-wrap;
  }
`;

const StyledChartContainer = styled(FlexContainer)<{ isDemo?: boolean }>`
  .canvas-wrapper {
    position: relative;

    &::before {
      position: absolute;
      content: '';
      height: 1px;
      width: 100%;
      bottom: var(--xsmall-padding);
      display: block;
      background-color: var(--gray-dark);
    }
  }

  .demo-overlay {
    position: relative;
    ${({ isDemo }) =>
      isDemo &&
      `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--purple-background);
      opacity: 0.5;
      z-index: 1;
    }
  `}
  }
`;

type SecurityCultureDashboardBarChartProps = {
  item: SurveyCategory | FilterableSurveyCategory;
  surveyId?: string;
  maxValue?: number;
  isLoading?: boolean;
  isMainDashboard?: boolean;
  isDemo?: boolean;
  isEmpty?: boolean;
};

export const SecurityCultureDashboardBarChart: React.FC<
  SecurityCultureDashboardBarChartProps
> = ({
  item,
  surveyId,
  maxValue,
  isLoading,
  isMainDashboard,
  isDemo,
  isEmpty,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isFilterable = 'category' in item;

  const tooltipText = () => {
    const change = isFilterable
      ? (item as FilterableSurveyCategory).score -
        ((item as FilterableSurveyCategory).previousScore ?? 0)
      : (item as SurveyCategory).result -
        (item as SurveyCategory).previousResult;
    if (change === 0) return t('security.culture.no.change');
    return change > 0
      ? `+ ${parseFloatingNumber(change)}`
      : `- ${parseFloatingNumber(Math.abs(change))}`;
  };

  const onChartHover = (event: any, activeElements: any[]) => {
    if (!isMainDashboard) return;
    event.native.target.style.cursor =
      activeElements.length > 0 ? 'pointer' : 'default';
  };
  const onChartClick = (event: any, activeElements: any[]) => {
    if (activeElements.length > 0 && isMainDashboard) {
      navigate(RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY]());
    }
  };

  return (
    <StyledChartContainer
      id={`sc-category-${isFilterable ? item.category : item.name}`}
      direction="column"
      key={isFilterable ? item.category : item.id}
      gap={isMainDashboard ? 0 : 10}
      className="relative"
      isDemo={isDemo}
    >
      {isLoading ? (
        <Skeleton
          className="chart"
          shape="rectangle"
          height={isMainDashboard ? '100px' : '200px'}
          width={getSecurityCultureChartWidth(!!isMainDashboard, false)}
        />
      ) : (
        <BarChart
          id={`sc-scores-${isFilterable ? item.category : item.id}`}
          value={parseFloatingNumber(isFilterable ? item.score : item.result)}
          secondaryValue={parseFloatingNumber(
            isFilterable ? item.benchmarkScore : item.benchmarkResult,
          )}
          displayXAxis={false}
          height={isMainDashboard ? '100px' : '200px'}
          width={getSecurityCultureChartWidth(
            !!isMainDashboard,
            (isFilterable ? item.benchmarkScore : item.benchmarkResult) > 0,
          )}
          tooltip={
            isFilterable &&
            (item as FilterableSurveyCategory)?.previousScore != null &&
            item
              ? t('security.culture.change.after.last.measurement', {
                  change: tooltipText(),
                })
              : undefined
          }
          defaultTooltip={t('security.culture.target.group')}
          benchmarkTooltip={t('security.culture.other.organisations')}
          maxValue={maxValue}
          isMainDashboard={isMainDashboard}
          isDemo={isDemo || isEmpty}
          isPurple
          onHover={onChartHover}
          onClick={onChartClick}
        />
      )}
      <FlexContainer direction="column" height="100%" justify="flex-start">
        <StyledLabelContainer
          align="flex-start"
          justify="center"
          className="m-0"
          isMainDashboard={isMainDashboard}
        >
          {t(
            `security.culture.category.${
              isFilterable ? item.category : item.name
            }.title`,
          )}
          <i
            id={`sc-scores-${isFilterable ? item.category : item.id}-info`}
            className="pi pi-info-circle ml-1"
            style={isMainDashboard ? { fontSize: '12px' } : undefined}
          />
        </StyledLabelContainer>
        <Tooltip
          target={`#sc-scores-${isFilterable ? item.category : item.id}-info`}
          position="bottom"
          content={t(
            `security.culture.category.${
              isFilterable ? item.category : item.name
            }.tooltip`,
          )}
        />
        {!isMainDashboard && !isDemo && (
          <StyledContainer
            direction="column"
            className="mt-auto pt-2"
            justify="flex-end"
          >
            <AppButton
              type="outlined"
              label={t('button.view.more')}
              onClick={() =>
                surveyId &&
                navigate(
                  RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_CATEGORY](
                    surveyId,
                    isFilterable ? item.category : item.name,
                  ),
                )
              }
            />
          </StyledContainer>
        )}
      </FlexContainer>
    </StyledChartContainer>
  );
};
